import { useCallback, useEffect, useMemo, useState } from "react";
import { setActiveModal } from "store/modal/slice";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { PlatformId } from "views/page-content/platforms/types";
import { RootState } from "store/reducers";
import { DestinationDescriptor } from "views/page-content/platforms/platform/simulcast/types";
import { resetAllForms, updateGeneralForm } from "store/platforms/slice";
import { FacebookEdge } from "api/facebook/types";
import {
    NewLivestreamModalSteps,
    SubmissionStatus,
    ValidationErrors
} from "./types";

export const useNewLivestreamStepLogic = ({
    reload
}: {
    reload: () => void;
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { general, videoplayer } = useSelector(
        (state: RootState) => state.platforms
    );

    const [activeStep, setActiveStep] = useState<NewLivestreamModalSteps>(
        NewLivestreamModalSteps.SelectDestination
    );

    const [selectedPlatform, setSelectedPlatform] = useState<PlatformId | null>(
        null
    );

    const [selectedDestinationId, setSelectedDestinationId] = useState<
        string | null
    >(null);

    const [currentFacebookEdge, setCurrentFacebookEdge] =
        useState<FacebookEdge>(null);

    const [onAdvanceStep, setOnAdvanceStep] = useState<(() => void) | null>(
        null
    );
    const [onDismiss, setOnDismiss] = useState<(() => void) | null>(null);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const [submissionStatus, setSubmissionStatus] =
        useState<SubmissionStatus | null>(null);

    useEffect(() => {
        if (submissionStatus) setHasSubmitted(true);
    }, [submissionStatus]);

    const clearCallbacks = useCallback(() => {
        setOnAdvanceStep(null);
        setOnDismiss(null);
    }, []);

    const [
        hasAddedPlatformSpecificSettings,
        setHasAddedPlatformSpecificSettings
    ] = useState<boolean>(false);

    const getPlatformSubsettingsModalHeader = useCallback(
        (platformId: PlatformId | null) => {
            if (!platformId) return null;

            switch (platformId) {
                case PlatformId.Youtube:
                    return t("platforms:channel-settings");
                case PlatformId.Twitch:
                    return t("platforms:twitch");
                case PlatformId.Facebook:
                    switch (currentFacebookEdge) {
                        case "accounts":
                            return t("facebook-destination:page-settings");
                        case "me":
                            return t("facebook-destination:timeline-settings");
                        default:
                            return t(`platforms:facebook`);
                    }
                default:
                    return t(`platforms:${selectedPlatform}`);
            }
        },
        [t, selectedPlatform, currentFacebookEdge]
    );

    const [validationErrors, setValidationErrors] = useState<
        ValidationErrors[]
    >([]);

    const validateLivestreamDetails = useCallback(() => {
        const now = new Date();
        const errors = [];

        // Destructure the necessary fields from the general form
        const {
            title,
            description,
            scheduledStartTime,
            selectedSimulcastDestinations
        } = general.generalForm;

        // All livestreams must have a title
        if (!title.length) {
            errors.push("title-required");
        }

        // The start time must be in the future
        if (scheduledStartTime <= now) {
            errors.push("start-time-must-be-in-future");
        }

        // Facebook requires a description
        const includesFb = selectedSimulcastDestinations.some(
            (d) => d.platform === PlatformId.Facebook
        );
        if (includesFb && !description.length) {
            errors.push("facebook-description-required");
        }

        if (errors.length) {
            setValidationErrors(errors);
            return false;
        }

        setValidationErrors([]);
        return true;
    }, [general.generalForm]);

    const advanceStep = useCallback(() => {
        if (onAdvanceStep) onAdvanceStep();

        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                setActiveStep(NewLivestreamModalSteps.LivestreamDetails);

                const currentDestinations =
                    general?.generalForm?.selectedSimulcastDestinations;

                const catalogOrCollectionsSelected =
                    general?.generalForm?.showInCatalog ||
                    videoplayer?.videoPlayerForm?.playlistIds.length;

                const notAlreadyAdded = !currentDestinations.some(
                    (d) => d.platform === PlatformId.VideoPlayer
                );

                if (notAlreadyAdded && catalogOrCollectionsSelected) {
                    const destinationDescriptor: DestinationDescriptor = {
                        name: "Player Page",
                        id: "videoplayer",
                        platform: PlatformId.VideoPlayer
                    };

                    dispatch(
                        updateGeneralForm({
                            selectedSimulcastDestinations: [
                                ...currentDestinations,
                                destinationDescriptor
                            ]
                        })
                    );

                    dispatch(
                        updateGeneralForm({
                            enableRecording: true
                        })
                    );
                } else if (!catalogOrCollectionsSelected) {
                    dispatch(
                        updateGeneralForm({
                            selectedSimulcastDestinations: [
                                ...currentDestinations.filter(
                                    (d) => d.platform !== PlatformId.VideoPlayer
                                )
                            ]
                        })
                    );

                    dispatch(
                        updateGeneralForm({
                            enableRecording: false
                        })
                    );
                }
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);

                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                const formIsValid = validateLivestreamDetails();
                if (formIsValid) {
                    setIsSubmitting(true);
                    setActiveStep(NewLivestreamModalSteps.Processing);
                }
                break;
            case NewLivestreamModalSteps.Processing:
                if (!isSubmitting) {
                    if (
                        [
                            SubmissionStatus.Success,
                            SubmissionStatus.Mixed
                        ].includes(submissionStatus)
                    ) {
                        setSubmissionStatus(null);
                        dispatch(resetAllForms());
                        setActiveStep(
                            NewLivestreamModalSteps.SelectDestination
                        );
                    } else if (submissionStatus === SubmissionStatus.Failure) {
                        setActiveStep(
                            NewLivestreamModalSteps.LivestreamDetails
                        );
                    }
                }
                break;
        }

        clearCallbacks();
    }, [
        activeStep,
        onAdvanceStep,
        clearCallbacks,
        validateLivestreamDetails,
        isSubmitting,
        submissionStatus,
        dispatch,
        general?.generalForm,
        videoplayer?.videoPlayerForm
    ]);

    const advanceButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("misc:next");
            case NewLivestreamModalSteps.PlatformSettings:
                return t("buttons:save");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:schedule-event");
            case NewLivestreamModalSteps.Processing:
                if (isSubmitting) return null;

                if (submissionStatus === SubmissionStatus.Failure) {
                    return t("buttons:back");
                } else {
                    return t("buttons:schedule-another");
                }
            default:
                return t("buttons:back");
        }
    }, [activeStep, t, submissionStatus, isSubmitting]);

    const dismissButtonText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("buttons:cancel");
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? t("buttons:back")
                    : t("buttons:cancel");
            case NewLivestreamModalSteps.LivestreamDetails:
                return t("buttons:back");
            case NewLivestreamModalSteps.Processing:
                if (
                    isSubmitting ||
                    submissionStatus === SubmissionStatus.Failure
                ) {
                    return t("buttons:cancel");
                } else {
                    return t("buttons:done");
                }
            default:
                return t("buttons:cancel");
        }
    }, [
        t,
        activeStep,
        isSubmitting,
        submissionStatus,
        hasAddedPlatformSpecificSettings
    ]);

    const reloadStreams = useCallback(() => {
        if (reload && hasSubmitted) reload();
    }, [reload, hasSubmitted]);

    const dismissButtonAction = useCallback(() => {
        if (onDismiss) onDismiss();
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                dispatch(setActiveModal(null));
                reloadStreams();
                break;
            case NewLivestreamModalSteps.PlatformSettings:
                if (!hasAddedPlatformSpecificSettings)
                    setActiveStep(NewLivestreamModalSteps.SelectDestination);
                setHasAddedPlatformSpecificSettings(false);
                setSelectedDestinationId(null);
                setCurrentFacebookEdge(null);
                break;
            case NewLivestreamModalSteps.LivestreamDetails:
                setValidationErrors([]);
                setActiveStep(NewLivestreamModalSteps.SelectDestination);
                break;
            case NewLivestreamModalSteps.Processing:
                if (isSubmitting) {
                    // TODO: ACTUAL CANCEL LOGIC?
                    setActiveStep(NewLivestreamModalSteps.LivestreamDetails);
                } else {
                    dispatch(resetAllForms());
                    dispatch(setActiveModal(null));
                    reloadStreams();
                    setActiveStep(NewLivestreamModalSteps.SelectDestination);
                }
                break;
        }
        clearCallbacks();
    }, [
        activeStep,
        dispatch,
        hasAddedPlatformSpecificSettings,
        onDismiss,
        clearCallbacks,
        isSubmitting,
        reloadStreams
    ]);

    const canProceedToNextStep = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                const hasAddedExternalPlatforms =
                    general.generalForm.selectedSimulcastDestinations.length >
                    0;

                const hasAddedCollections =
                    videoplayer?.videoPlayerForm?.playlistIds.length > 0;

                return (
                    hasAddedExternalPlatforms ||
                    hasAddedCollections ||
                    general.generalForm?.showInCatalog
                );
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings;
            case NewLivestreamModalSteps.LivestreamDetails:
            case NewLivestreamModalSteps.Processing:
                // "Schedule Event" is always enabled - validation occurs on submission
                // Advance action is always enabled in Processing step
                return true;
            default:
                return false;
        }
    }, [activeStep, general, hasAddedPlatformSpecificSettings, videoplayer]);

    const modalHeaderText = useMemo(() => {
        switch (activeStep) {
            case NewLivestreamModalSteps.SelectDestination:
                return t("platforms:new-livestream");
            case NewLivestreamModalSteps.PlatformSettings:
                return hasAddedPlatformSpecificSettings
                    ? getPlatformSubsettingsModalHeader(selectedPlatform)
                    : t(`platforms:${selectedPlatform}`);
            case NewLivestreamModalSteps.LivestreamDetails:
            case NewLivestreamModalSteps.Processing:
            default:
                return t("platforms:new-livestream");
        }
    }, [
        activeStep,
        t,
        selectedPlatform,
        hasAddedPlatformSpecificSettings,
        getPlatformSubsettingsModalHeader
    ]);

    return {
        isSubmitting,
        setIsSubmitting,
        setSubmissionStatus,
        activeStep,
        setActiveStep,
        setSelectedPlatform,
        selectedDestinationId,
        setSelectedDestinationId,
        selectedPlatform,
        setHasAddedPlatformSpecificSettings,
        setCurrentFacebookEdge,
        validationErrors,
        setOnAdvanceStep,
        advanceStep,
        advanceButtonText,
        setOnDismiss,
        dismissButtonAction,
        dismissButtonText,
        canProceedToNextStep,
        modalHeaderText
    };
};
